import isEqual from 'lodash-es/isEqual';

import { create, destroy, v } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/widgets/middleware/theme';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { dimensions } from '@dojo/framework/core/middleware/dimensions';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { Chart } from 'chart.js';
import 'chartjs-plugin-colorschemes';
import 'chartjs-plugin-doughnutlabel';

import { ChartProperties } from '../interfaces';

import { canvasnode } from '../support/canvas-middleware';

import css from '../css/charts.m.css';

interface ChartBaseIcache {
	chart: Chart;
}

const icache = createICacheMiddleware<ChartBaseIcache>();
// const nls = createNlsMiddleware('settings');
const factory = create({ icache, destroy, theme, dimensions, canvasnode }).properties<ChartProperties>();

export const ChartBase = factory(({ properties, middleware: { icache, destroy, theme, dimensions, canvasnode } }) => {
	const createChart = (canvasNode: HTMLCanvasElement): void => {
		const {
			data,
			legend,
			options,
			plugins: pluginsProp,
			pluginCode,
			title,
			type: typeProp,
			drawComplete
		} = properties();
		const type = typeProp || 'bar';

		// default values if they aren't set in options
		if (options.tooltips === undefined) {
			options.tooltips = { mode: 'nearest' };
		}
		if (options.responsive === undefined) {
			options.responsive = true;
		}
		if (options.scales === undefined) {
			options.scales = {
				yAxes: [{ ticks: { beginAtZero: true } }]
			};
		}

		if (options.onClick !== undefined) {
			// eslint-disable-next-line @typescript-eslint/ban-types
			const realOnclick: (event?: MouseEvent, activeElements?: Array<{}>, chart?: Chart) => void =
				options.onClick;
			// eslint-disable-next-line @typescript-eslint/ban-types
			options.onClick = (event?: MouseEvent, activeElements?: Array<{}>) => {
				const chart = icache.get('chart');
				realOnclick(event, activeElements, chart);
			};
		}

		if (pluginsProp) {
			options.plugins = pluginsProp;
		} else if (options.plugins === undefined) {
			options.plugins = {
				colorschemes: {
					scheme: 'tableau.ColorBlind10'
				}
			};
		}

		if (legend) {
			options.legend = legend;
		} else {
			options.legend = { display: true, position: 'bottom' };
		}

		if (title) {
			options.title = {
				display: true,
				text: title
			};
		}

		const drawPlugin = {
			afterDraw: () => {
				drawComplete && drawComplete();
			}
		};

		const chartParams: { [key: string]: any } = {
			type,
			data,
			options
		};
		if (pluginCode && pluginCode.length > 0) {
			chartParams.plugins = [...pluginCode, drawPlugin];
		} else if (drawComplete) {
			chartParams.plugins = [drawPlugin];
		}

		icache.set('chart', new Chart(canvasNode, chartParams));

		destroy(() => {
			const chart = icache.get('chart');
			chart && chart.destroy();
			icache.delete('chart');
		});
	};

	const updateChart = (): void => {
		const chart = icache.get('chart');
		if (chart === undefined) {
			return;
		}
		const { data, type } = properties();

		if (type && chart.config.type !== type) {
			chart.config.type = type;
		}

		const chartdata = chart.config.data;
		if (data && data.datasets) {
			if (chartdata !== undefined && chartdata.datasets) {
				data.datasets.forEach((ds, idx) => {
					if (!isEqual(chartdata.datasets![idx], ds)) {
						chartdata.datasets![idx] = { ...ds };
					}
				});
			} else {
				chart.config.data = data;
			}
		}

		chart.update();
	};

	const { ariaLabel = 'chart', size, type, alternateText = '' } = properties();
	const canvasNode = canvasnode.get('canvas-node');
	if (canvasNode) {
		const chart = icache.get('chart');
		chart ? updateChart() : requestAnimationFrame(() => createChart(canvasNode));
	}

	let width: number;
	let height: number;
	if (size === undefined) {
		const { client } = dimensions.get('root');
		width = client.width;
		height = client.height;
		if (height === 0) {
			height = width / 2;
		}
	} else {
		width = size.width;
		height = size.height;
	}

	const themeCss = theme.classes(css);

	return v('div', { key: 'root', xqa: `chart ${type}`, classes: [themeCss.root] }, [
		v(
			'canvas',
			{
				key: 'canvas-node',
				'aria-label': ariaLabel,
				role: 'img',
				tabIndex: 0,
				classes: [themeCss.chart],
				width,
				height
			},
			[alternateText]
		)
	]);
});
